import { useState, type ChangeEvent, type ComponentProps, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from 'src/components/ui/button';

type IncludedAppsFieldProps = Pick<ComponentProps<'input'>, 'id' | 'aria-describedby' | 'aria-invalid'> & {
  value: string[];
  onChange: (value: string[]) => void;
};

const IncludedAppsField = ({ value, onChange, ...inputProps }: IncludedAppsFieldProps) => {
  const [text, setText] = useState('');

  const commit = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return;
    }
    const isDuplicate = value.some((app) => app.toLowerCase() === trimmed.toLowerCase());
    if (!isDuplicate) {
      onChange([...value, trimmed]);
    }
    setText('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(text);
    }
  };

  const handleRemove = (app: string) => onChange(value.filter((includedApp) => includedApp !== app));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (nextValue.endsWith(',')) {
      commit(nextValue.slice(0, -1));
      return;
    }
    setText(nextValue);
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((app) => (
            <Badge key={app} variant="secondary" className="font-normal bg-muted gap-1.5 py-1 pl-2.25 pr-1.5">
              {app}
              <Button
                variant="ghost"
                type="button"
                onClick={() => handleRemove(app)}
                aria-label={`Remove ${app}`}
                className="w-3.5 h-3.5 p-0 hover:bg-border"
              >
                <X className="size-2.5! text-slate-400" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...inputProps}
        placeholder="e.g. Word, Excel, PowerPoint"
      />
    </div>
  );
};

export default IncludedAppsField;
